<script setup lang="ts">
import { cloneDeep } from 'lodash';
import { useDialogPluginComponent } from 'quasar';
import { computed, onBeforeUnmount, ref, toRef } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n';

import { useLanguageValidationErrors } from '@/composables';
import { mergeImageCrop } from '@/helpers/image.helper';
import { localizableString } from '@/helpers/validation.helper';
import type { ContactListMemberDto, ContactListMemberSocialsDto, Locales } from '@/services/types/api.types';
import type { FileInfo } from '@/services/types/file.types';
import type { Metadata, SavingOptions } from '@/services/types/file-uploader.types';
import { useContactListMembersStore } from '@/stores/contactListMembers.store';
import { wrapSavingOperation } from '@/helpers/notifications.helper';

import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import { provideEditLocale } from '@/composables/data-display-utils';
import StandardInput from '@/components/StandardInput.vue';
import ImageField, { type CroppingInfo } from '@/components/ImageField.vue';

// STORE
const { t } = useI18n();
const contactListMemberStore = useContactListMembersStore();

// PROPS
const props = defineProps<{
  mode: 'add' | 'edit';
  contactListMember: ContactListMemberDto;
  languageEnabled: Locales[];
}>();

// EMITS
defineEmits([...useDialogPluginComponent.emits]);

// CONST
const PROFILE_IMAGE_CROPPING: CroppingInfo = {
  aspectRatio: 1,
  size: { width: 200, height: 200 },
};
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();
const initialValues = props.contactListMember;
const pendingProfileImageFiles = ref<File[] | null>(null);
const pendingProfileImageMetadata = ref<Metadata | null>(null);
const profileImagePreviewObjectUrl = ref<string | null>(null);
const profileImagePreview = ref<FileInfo | null>(null);
const { handleSubmit, errors, meta, setFieldValue, values } = useForm({
  initialValues: initialValues,
  validationSchema: yup.object<ContactListMemberDto>({
    name: localizableString(props.languageEnabled),
    role: localizableString(props.languageEnabled),
    organization: localizableString(props.languageEnabled, { optional: true }),
    description: localizableString(props.languageEnabled, { optional: true }),
    email: yup.string().email().nullable(),
    phone: yup.string().nullable(),
    profile_image: yup.mixed<FileInfo>().nullable(),
    socials: yup
      .object<ContactListMemberSocialsDto>({
        facebook: yup.string().url().nullable(),
        linkedIn: yup.string().url().nullable(),
        x: yup.string().url().nullable(),
        instagram: yup.string().url().nullable(),
      })
      .required(),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);
const editModel = ref(cloneDeep(props.contactListMember));
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

// COMPUTED
const dialogTitle = computed(() =>
  props.mode === 'add' ? t('project-module.contactList.dialogs.create') : t('project-module.contactList.dialogs.edit'),
);
const isDirty = computed(() => meta.value.dirty);
const displayedProfileImage = computed<FileInfo | null>(
  () => profileImagePreview.value ?? (values.profile_image as FileInfo | null) ?? null,
);

// FUNCTIONS
const revokeProfileImagePreview = () => {
  if (profileImagePreviewObjectUrl.value) {
    URL.revokeObjectURL(profileImagePreviewObjectUrl.value);
    profileImagePreviewObjectUrl.value = null;
  }
};

const buildProfileImagePreview = (file: File, metadata: Metadata | null): FileInfo => {
  const imageMetadata = metadata?.image ? { ...metadata.image, type: 'crop' as const } : { type: 'raw' as const };

  return {
    url: profileImagePreviewObjectUrl.value!,
    type: 'image',
    version: 'v2',
    metadata: {
      filename: file.name,
      mimetype: file.type || 'image/jpeg',
      size: file.size,
      image: imageMetadata,
    },
  };
};

const handleSaveProfileImage = async (options: SavingOptions) => {
  if (options.files?.length) {
    pendingProfileImageFiles.value = options.files;
  }

  if (options.metadata) {
    pendingProfileImageMetadata.value = options.metadata;
  }

  const previewFile = pendingProfileImageFiles.value?.[0];
  if (!previewFile) {
    const currentProfileImage = (values.profile_image as FileInfo | null) ?? props.contactListMember.profile_image;
    if (!currentProfileImage || !pendingProfileImageMetadata.value?.image) return;

    setFieldValue('profile_image', mergeImageCrop(currentProfileImage, pendingProfileImageMetadata.value));
    return;
  }

  revokeProfileImagePreview();
  profileImagePreviewObjectUrl.value = URL.createObjectURL(previewFile);
  profileImagePreview.value = buildProfileImagePreview(previewFile, pendingProfileImageMetadata.value);
};

const handleClearProfileImage = async () => {
  pendingProfileImageFiles.value = null;
  pendingProfileImageMetadata.value = null;
  profileImagePreview.value = null;
  revokeProfileImagePreview();
  setFieldValue('profile_image', null);
};

const buildSaveOptions = (): SavingOptions | undefined => {
  if (!pendingProfileImageMetadata.value) return undefined;
  return {
    version: 'v2',
    files: pendingProfileImageFiles.value,
    metadata: pendingProfileImageMetadata.value,
  };
};

const onSubmit = handleSubmit(async (values) => {
  await wrapSavingOperation(
    async () => await contactListMemberStore.saveContactListMember(values, buildSaveOptions()),
    () => onDialogOK(),
  );
});

onBeforeUnmount(() => {
  revokeProfileImagePreview();
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent class="contact-list-member-dialog">
    <q-card
      class="q-dialog-plugin contact-list-member-dialog__card"
      v-if="editModel"
      style="width: min(1280px, 96vw); max-width: 96vw"
    >
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section class="contact-list-member-dialog__content">
        <div class="contact-list-member-dialog__content-grid">
          <div class="contact-list-member-dialog__column">
            <localized-input
              name="name"
              :label="t('global.label.name')"
              :editLocale="editLocale"
              :language_enabled="languageEnabled"
              outlined
            />
            <localized-input
              name="role"
              :label="t('global.label.role')"
              :editLocale="editLocale"
              :language_enabled="languageEnabled"
              outlined
            />
            <localized-input
              name="organization"
              :label="t('global.label.organization')"
              :editLocale="editLocale"
              :language_enabled="languageEnabled"
              outlined
            />
            <q-field :label="t('global.label.description')" stack-label borderless>
              <template v-slot:control>
                <localized-editor
                  name="description"
                  :label="t('global.label.description')"
                  class="q-mt-sm full-width"
                  :editLocale="editLocale"
                  :language_enabled="languageEnabled"
                />
              </template>
            </q-field>
          </div>
          <div class="contact-list-member-dialog__column">
            <standard-input name="email" :label="t('global.label.email')" outlined />
            <standard-input name="phone" :label="t('global.label.phone')" outlined />
            <image-field
              version="v2"
              :cropping="PROFILE_IMAGE_CROPPING"
              :file="displayedProfileImage"
              :form-dirty="isDirty"
              :label="t('global.label.image')"
              :language-enabled="languageEnabled"
              @clear-image="handleClearProfileImage"
              @save-image="handleSaveProfileImage"
            />
          </div>
        </div>
        <q-separator :spaced="true" />
        <p class="contact-list-member-dialog__socials-title">{{ t('global.label.socials') }}</p>
        <div class="contact-list-member-dialog__content-grid">
          <div class="contact-list-member-dialog__column">
            <standard-input name="socials.facebook" label="Facebook" outlined />
            <standard-input name="socials.linkedIn" label="LinkedIn" outlined />
          </div>
          <div class="contact-list-member-dialog__column">
            <standard-input name="socials.x" label="X" outlined />
            <standard-input name="socials.instagram" label="Instagram" outlined />
          </div>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style lang="scss">
.contact-list-member-dialog {
  &__card {
    max-height: calc(100vh - 48px);
    display: flex;
    flex-direction: column;
  }

  &__content {
    overflow-y: auto;
  }

  &__content-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 24px;
    align-items: start;
  }

  &__column {
    min-width: 0;
  }

  &__socials-title {
    margin: 0 0 8px;
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.4rem;
  }
}
</style>
